export const drawBuildingsArticle = {
    id: 'draw-buildings',
    title: 'Здания',
    category: 'Режим рисования',
    requiredModule: null,
    sandboxType: null,
    summary: 'Объекты капитального строительства: 3D-высота, пятно застройки, статус «В строительстве», категории и паспорта.',
    markdown: `
> **Инструмент «Здания»** предназначен для нанесения объектов капитального строительства (ОКС) и любых надземных построек. В отличие от плоских участков, здания сразу получают вертикальный объём и эффектно отображаются в 3D-виде при наклоне карты.

---

## 1. Сценарии использования

:::grid cols=2
:::card icon="home" title="1. Коттеджи и частные дома"
Нанесение жилых домов на участках: с привязкой проекта, планировки, материалов стен, этажности и фотографий фасадов.
:::

:::card icon="layers" title="2. Таунхаусы и секции"
Очерчивание блокированных домов, дуплексов и многосекционных жилых комплексов с делением по секциям.
:::

:::card icon="settings" title="3. Коммерция и администрация"
Здания инфраструктуры: офисы продаж, въездные группы и КПП, магазины, спа-комплексы и рестораны.
:::

:::card icon="box" title="4. Хозпостройки и гаражи"
Вспомогательные строения на территории: отдельно стоящие бани, гаражные боксы, навесы, беседки и котельные.
:::
:::

---

## 2. 3D-объём и высота: как здания «вырастают» на карте

Каждое здание в системе является трёхмерным объектом. Главный параметр, управляющий его видом — поле **«Высота (м)»** в Паспорте:

:::card icon="box" title="Как работает 3D-визуализация:"
* **Высота по умолчанию:** если высота не заполнена вручную, здание сразу получает базовую высоту **5 метров** и не остаётся плоским.
* **Усиление рельефа (×5):** для выразительного и наглядного восприятия на масштабной спутниковой карте система визуально отображает высоту с коэффициентом умножения. Благодаря этому даже 1-2 этажные дома отчётливо видны и не сливаются с рельефом при наклоне и вращении карты.
* **Ручная настройка:** в паспорте объекта вы можете указать реальную высоту в метрах (например, \`3.5\`, \`7\` или \`12\`) — здание мгновенно пропорционально изменит свой размер в 3D.
:::

---

## 3. Площадь здания: как она рассчитывается?

* **Площадь пятна застройки:** система автоматически рассчитывает площадь **горизонтального контура фундамента (м²)**.
* **Без умножения на высоту:** высота не перемножается с площадью автоматически, чтобы не искажать точные геодезические замеры посадки дома на участке.
* **Общая площадь дома:** если вам нужно зафиксировать суммарную жилую площадь всех этажей (например, 180 м² при фундаменте 100 м²), её можно указать в поле описания или дополнительных характеристиках паспорта.
* **Автозаполнение при создании:** как и у участков, площадь автоматически подставляется при первом создании контура, а ваши ручные правки бережно сохраняются.

---

## 4. Статусы здания: стройка, продажи и планы

В паспорте здания доступен статус **«В строительстве»**, который отражает физический процесс возведения объекта:

| Статус | Как выглядит | Что означает |
| :--- | :---: | :--- |
| **В продаже** | 🟢 Зелёный бейдж | Дом готов или строится и открыт для бронирования/покупки на публичной карте. |
| **В строительстве** | 🏗️ Оранжевый бейдж | **Ключевой статус:** на объекте ведутся активные строительные работы (фундамент, коробка, кровля, отделка). |
| **Запланировано** | 🟡 Жёлтый бейдж | Проектный дом: контур нанесён в генплан, но строительные работы ещё не начаты. |
| **На учёте** | ⚪ Без отметки | Эксплуатируемые, технические или вспомогательные строения посёлка. |

:::tip
Сочетание статусов позволяет гибко вести продажи: например, включить одновременно **«В продаже»** и **«В строительстве»**, чтобы покупатели видели, что дом активно строится и доступен к покупке.
:::

---

## 5. Категории и оформление фасадов

В паспорте зданию назначается категория из классификатора (*«Жилое»*, *«Административное»*, *«Хозяйственное»* и т.д.):
* Категория задаёт цвет стен здания на карте.
* Для строящихся или проектных зданий можно использовать штриховку, чтобы визуально выделить их на фоне готовых домов.
`
};
